import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import Alert from "@material-ui/lab/Alert"
import AddIcon from "@material-ui/icons/Add"
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward"
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward"
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard"
import DeleteIcon from "@material-ui/icons/Delete"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import LocalOfferIcon from "@material-ui/icons/LocalOffer"
import LockIcon from "@material-ui/icons/Lock"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { useClubeBeneficiosContext } from "../context/useClubeBeneficiosContext"
import {
    ClubeBeneficiosActionsTypes,
    type CampoBeneficioEditavel,
    type CampoClubeEditavel,
} from "../context/clubeBeneficiosActions"
import {
    algumPlanoAtivoSemRecebedor,
    QUANTIDADE_MAXIMA_CUPONS,
    rotuloPlano,
    textoPrevia,
    validarClube,
    type BeneficioForm,
    type PlanoClubeForm,
} from "../models/clubeBeneficiosModel"

const useStyles = makeStyles(theme => ({
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardHeaderAcoes: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
    },
    cardContent: {
        padding: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    salvarBtn: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        alignSelf: "flex-end",
    },
    row: {
        display: "flex",
        gap: theme.spacing(2),
        [theme.breakpoints.down("xs")]: { flexDirection: "column" },
    },
    secaoBeneficios: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    beneficio: {
        border: "1px solid #e9ecef",
        borderRadius: 12,
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
    },
    beneficioTopo: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    previa: {
        backgroundColor: "#e8f5e9",
        color: "#1b5e20",
        borderRadius: 12,
        padding: theme.spacing(2),
        fontWeight: 500,
    },
    ajuda: {
        color: "#7f8c8d",
        margin: 0,
        paddingLeft: theme.spacing(2.5),
        "& li": { marginBottom: theme.spacing(0.5) },
    },
    semBeneficios: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(2),
    },
    planoBloqueado: {
        backgroundColor: "#f5f5f5",
        border: "2px dashed #bdbdbd",
        boxShadow: "none",
    },
    conteudoBloqueado: {
        opacity: 0.55,
    },
    chipBloqueado: {
        marginLeft: theme.spacing(1),
        backgroundColor: "#757575",
        color: "white",
        "& .MuiChip-icon": { color: "white" },
    },
    avisoBloqueado: {
        borderRadius: 0,
    },
    topoPlanos: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}))

function BeneficioItem({
    chavePlano,
    beneficio,
    indice,
    bloqueado,
}: {
    chavePlano: string
    beneficio: BeneficioForm
    indice: number
    bloqueado: boolean
}) {
    const classes = useStyles()
    const { dispatch } = useClubeBeneficiosContext()

    const atualizar = (campo: CampoBeneficioEditavel, valor: string | boolean) =>
        dispatch({
            type: ClubeBeneficiosActionsTypes.ATUALIZAR_BENEFICIO,
            payload: { chavePlano, chave: beneficio.chave, campo, valor },
        })

    return (
        <Box className={classes.beneficio}>
            <Box className={classes.beneficioTopo}>
                <Typography variant="subtitle2" style={{ display: "flex", alignItems: "center" }}>
                    <LocalOfferIcon style={{ color: "#4caf50", marginRight: 6, fontSize: 18 }} />
                    Benefício {indice + 1}: cupons de desconto
                </Typography>
                <Box display="flex" alignItems="center">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={beneficio.ativo}
                                disabled={bloqueado}
                                onChange={e => atualizar("ativo", e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label="Ativo"
                    />
                    <Tooltip title="Remover benefício">
                        <span>
                            <IconButton
                                size="small"
                                disabled={bloqueado}
                                aria-label={`Remover benefício ${indice + 1}`}
                                onClick={() =>
                                    dispatch({
                                        type: ClubeBeneficiosActionsTypes.REMOVER_BENEFICIO,
                                        payload: { chavePlano, chave: beneficio.chave },
                                    })
                                }
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Box className={classes.row}>
                <TextField
                    label="Quantidade de cupons por mês"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={bloqueado}
                    type="number"
                    inputProps={{ min: 1, max: QUANTIDADE_MAXIMA_CUPONS, step: 1 }}
                    value={beneficio.quantidade}
                    onChange={e => atualizar("quantidade", e.target.value)}
                />
                <TextField
                    select
                    label="Tipo do desconto"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={bloqueado}
                    SelectProps={{ native: true }}
                    value={beneficio.percentual ? "PERCENTUAL" : "VALOR"}
                    onChange={e => atualizar("percentual", e.target.value === "PERCENTUAL")}
                >
                    <option value="VALOR">Valor fixo (R$)</option>
                    <option value="PERCENTUAL">Percentual (%)</option>
                </TextField>
                <TextField
                    label="Valor do cupom"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={bloqueado}
                    value={beneficio.valor}
                    onChange={e => atualizar("valor", e.target.value)}
                    InputProps={
                        beneficio.percentual
                            ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                            : { startAdornment: <InputAdornment position="start">R$</InputAdornment> }
                    }
                />
            </Box>

            <Box className={classes.row}>
                <TextField
                    label="Pedido mínimo (opcional)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={bloqueado}
                    value={beneficio.valorMinimoPedido}
                    onChange={e => atualizar("valorMinimoPedido", e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                />
                {beneficio.percentual && (
                    <TextField
                        label="Desconto máximo (opcional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={bloqueado}
                        value={beneficio.maximoDesconto}
                        onChange={e => atualizar("maximoDesconto", e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

function PlanoCard({
    plano,
    indice,
    total,
    onExcluir,
}: {
    plano: PlanoClubeForm
    indice: number
    total: number
    onExcluir: (plano: PlanoClubeForm, indice: number) => void
}) {
    const classes = useStyles()
    const { dispatch } = useClubeBeneficiosContext()
    const rotulo = rotuloPlano(plano, indice)
    const chavePlano = plano.chave
    // Plano desativado: só a chave Ativo, a ordem e o excluir continuam disponíveis.
    const bloqueado = !plano.ativo

    const atualizarCampo = (campo: CampoClubeEditavel, valor: string | boolean) =>
        dispatch({
            type: ClubeBeneficiosActionsTypes.ATUALIZAR_CAMPO,
            payload: { chavePlano, campo, valor },
        })

    const mover = (direcao: -1 | 1) =>
        dispatch({ type: ClubeBeneficiosActionsTypes.MOVER_PLANO, payload: { chavePlano, direcao } })

    return (
        <Box
            className={`${classes.configCard} ${bloqueado ? classes.planoBloqueado : ""}`}
            component={Paper}
            data-testid="clube-plano"
        >
            <Box className={classes.cardHeader}>
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    <CardGiftcardIcon className={classes.iconHeader} />
                    {rotulo}
                    {bloqueado && (
                        <Chip
                            icon={<LockIcon />}
                            label="Desativado"
                            size="small"
                            className={classes.chipBloqueado}
                        />
                    )}
                </Typography>
                <Box className={classes.cardHeaderAcoes}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={plano.ativo}
                                onChange={e => atualizarCampo("ativo", e.target.checked)}
                                color="primary"
                                inputProps={{ "aria-label": `${rotulo} ativo` }}
                            />
                        }
                        label={plano.ativo ? "Ativo" : "Desativado"}
                    />
                    <Tooltip title="Mover para cima">
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Mover ${rotulo} para cima`}
                                disabled={indice === 0}
                                onClick={() => mover(-1)}
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Mover para baixo">
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Mover ${rotulo} para baixo`}
                                disabled={indice === total - 1}
                                onClick={() => mover(1)}
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Excluir plano">
                        <IconButton
                            size="small"
                            aria-label={`Excluir ${rotulo}`}
                            onClick={() => onExcluir(plano, indice)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            {bloqueado && (
                <Alert severity="warning" icon={<LockIcon />} className={classes.avisoBloqueado}>
                    Plano desativado: não aparece para os clientes e a edição está bloqueada. Ligue a
                    chave <strong>Ativo</strong> para editar.
                </Alert>
            )}
            <Box className={`${classes.cardContent} ${bloqueado ? classes.conteudoBloqueado : ""}`}>
                <Box className={classes.row}>
                    <TextField
                        label="Nome do plano"
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={bloqueado}
                        value={plano.nome}
                        onChange={e => atualizarCampo("nome", e.target.value)}
                    />
                    <TextField
                        label="Valor mensal"
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={bloqueado}
                        value={plano.valorMensal}
                        onChange={e => atualizarCampo("valorMensal", e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                    />
                </Box>
                <TextField
                    label="Descrição (aparece para o cliente no app)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={bloqueado}
                    multiline
                    minRows={2}
                    value={plano.descricao}
                    onChange={e => atualizarCampo("descricao", e.target.value)}
                />

                <Box className={classes.secaoBeneficios}>
                    <Typography variant="subtitle1" className={classes.cardHeaderTitle}>
                        <LocalOfferIcon className={classes.iconHeader} />
                        Benefícios do assinante
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={bloqueado}
                        startIcon={<AddIcon />}
                        onClick={() =>
                            dispatch({
                                type: ClubeBeneficiosActionsTypes.ADICIONAR_BENEFICIO,
                                payload: { chavePlano },
                            })
                        }
                    >
                        Adicionar cupons
                    </Button>
                </Box>

                {plano.beneficios.length === 0 ? (
                    <Typography variant="body2" className={classes.semBeneficios}>
                        Nenhum benefício cadastrado. Clique em "Adicionar cupons" para definir os
                        cupons que o assinante deste plano recebe todo mês.
                    </Typography>
                ) : (
                    plano.beneficios.map((b, i) => (
                        <BeneficioItem
                            key={b.chave}
                            chavePlano={chavePlano}
                            beneficio={b}
                            indice={i}
                            bloqueado={bloqueado}
                        />
                    ))
                )}

                <Typography className={classes.previa}>{textoPrevia(plano)}</Typography>
            </Box>
        </Box>
    )
}

export function FormClube() {
    const classes = useStyles()
    const { dados, dispatch, salvar, erroCarregamento } = useClubeBeneficiosContext()
    const { enqueueSnackbar } = useSnackbar()
    const [excluindo, setExcluindo] = useState<{ plano: PlanoClubeForm; rotulo: string } | null>(
        null
    )

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const bloqueadoSemRecebedor = algumPlanoAtivoSemRecebedor(dados)

    const removerPlano = (chavePlano: string) =>
        dispatch({ type: ClubeBeneficiosActionsTypes.REMOVER_PLANO, payload: { chavePlano } })

    const pedirExclusao = (plano: PlanoClubeForm, indice: number) => {
        // Plano ainda não salvo some direto; plano salvo pede confirmação.
        if (!plano.id) {
            removerPlano(plano.chave)
            return
        }
        setExcluindo({ plano, rotulo: rotuloPlano(plano, indice) })
    }

    const confirmarExclusao = () => {
        if (excluindo) removerPlano(excluindo.plano.chave)
        setExcluindo(null)
    }

    const handleSalvar = async () => {
        const erro = validarClube(dados)
        if (erro) {
            enqueueSnackbar(erro, { variant: "warning" })
            return
        }
        try {
            await salvar()
            enqueueSnackbar("Clube de Benefícios salvo com sucesso!", { variant: "success" })
        } catch (e) {
            enqueueSnackbar((e as Error).message || "Erro ao salvar o Clube de Benefícios.", {
                variant: "error",
            })
        }
    }

    const qtdExcluidos = dados.planosExcluidos.length

    return (
        <>
            {!dados.recebedorConfigurado && (
                <Box mb={3}>
                    <Alert severity="warning">
                        Para ativar o Clube de Benefícios é preciso configurar o{" "}
                        <strong>recebedor Pagar.me</strong> da sua loja com a Cliente Fiel (é por ele
                        que as mensalidades caem na sua conta). Entre em contato com o suporte da
                        Cliente Fiel. Enquanto isso, você pode montar e salvar os planos desativados.
                    </Alert>
                </Box>
            )}

            <Box className={classes.topoPlanos}>
                <Typography variant="body2" style={{ color: "#7f8c8d", flex: 1, minWidth: 220 }}>
                    Cadastre um ou mais planos (ex.: R$ 10/mês com 4 cupons, R$ 20/mês com 8
                    cupons). O cliente vê os planos ativos no app nesta ordem.
                </Typography>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => dispatch({ type: ClubeBeneficiosActionsTypes.ADICIONAR_PLANO })}
                >
                    Adicionar plano
                </Button>
            </Box>

            {qtdExcluidos > 0 && (
                <Box mb={3}>
                    <Alert
                        severity="info"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() =>
                                    dispatch({
                                        type: ClubeBeneficiosActionsTypes.RESTAURAR_PLANOS_EXCLUIDOS,
                                    })
                                }
                            >
                                Desfazer
                            </Button>
                        }
                    >
                        {qtdExcluidos === 1
                            ? "1 plano será excluído ao salvar."
                            : `${qtdExcluidos} planos serão excluídos ao salvar.`}{" "}
                        Planos que já tiveram assinaturas não podem ser excluídos; nesse caso,
                        desative-os.
                    </Alert>
                </Box>
            )}

            {dados.planos.length === 0 ? (
                <Box className={classes.configCard} component={Paper}>
                    <Typography variant="body2" className={classes.semBeneficios}>
                        Nenhum plano cadastrado. Clique em "Adicionar plano" para criar o primeiro.
                    </Typography>
                </Box>
            ) : (
                dados.planos.map((p, i) => (
                    <PlanoCard
                        key={p.chave}
                        plano={p}
                        indice={i}
                        total={dados.planos.length}
                        onExcluir={pedirExclusao}
                    />
                ))
            )}

            <Box className={classes.configCard} component={Paper}>
                <Box className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                        <InfoOutlinedIcon className={classes.iconHeader} />
                        Como funciona
                    </Typography>
                </Box>
                <Box className={classes.cardContent}>
                    <ul className={classes.ajuda}>
                        <li>
                            <Typography variant="body2">
                                Mudar o valor mensal só vale para <strong>novos assinantes</strong>;
                                quem já assina continua pagando o valor da adesão.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                O cliente só pode ter <strong>um plano por vez</strong>; para trocar,
                                ele cancela o atual e assina outro.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Os cupons são gerados a cada mensalidade paga e valem{" "}
                                <strong>só durante o ciclo pago</strong> (não acumulam para o mês
                                seguinte).
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                O cliente só vê um plano no app quando ele está{" "}
                                <strong>ativo</strong>, com valor mensal e pelo menos um benefício
                                ativo.
                            </Typography>
                        </li>
                    </ul>

                    {bloqueadoSemRecebedor && (
                        <Alert severity="error">
                            Não é possível salvar planos ativos sem o recebedor Pagar.me configurado.
                            Desative os planos para salvar.
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        className={classes.salvarBtn}
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                        disabled={bloqueadoSemRecebedor}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>

            <Dialog open={!!excluindo} onClose={() => setExcluindo(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Excluir plano</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Excluir o <strong>{excluindo?.rotulo}</strong>? A exclusão só acontece quando
                        você clicar em <strong>Salvar</strong>. Se o plano já teve assinaturas, ele
                        não pode ser excluído; nesse caso, desative-o.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExcluindo(null)}>Voltar</Button>
                    <Button color="secondary" variant="contained" onClick={confirmarExclusao}>
                        Excluir plano
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
